import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, X as XIcon, Bell, Share2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ShareAppLink } from "@/components/ShareApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const TIME_OPTIONS = HOURS.map((h) => {
  const hh = String(h).padStart(2, "0");
  return { value: `${hh}:00`, label: `${hh}:00` };
});

export default function SettingsDialog({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "",
    notifications_enabled: true,
    briefing_time: "08:00",
    default_duration_minutes: "none",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        notifications_enabled: user.notifications_enabled !== false,
        briefing_time: user.briefing_time || "08:00",
        default_duration_minutes: user.default_duration_minutes
          ? String(user.default_duration_minutes)
          : "none",
      });
    }
  }, [user, open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/me`, {
        name: form.name.trim() || null,
        notifications_enabled: form.notifications_enabled,
        briefing_time: form.briefing_time,
        default_duration_minutes:
          form.default_duration_minutes && form.default_duration_minutes !== "none"
            ? parseInt(form.default_duration_minutes, 10)
            : null,
      });
      // Request notification permission if user enabled it
      if (
        form.notifications_enabled &&
        "Notification" in window &&
        Notification.permission === "default"
      ) {
        await Notification.requestPermission();
      }
      await refresh();
      toast.success("Settings saved");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-none max-w-md p-0 gap-0 border border-zinc-300"
        data-testid="settings-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
          <DialogTitle className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
            Settings
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
              Display name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="w-full h-11 px-3 text-sm border border-zinc-300 focus:border-[#002FA7] outline-none"
              data-testid="settings-name-input"
            />
            <p className="text-xs text-zinc-400 mt-1">{user?.email}</p>
          </div>

          <div className="flex items-start justify-between gap-3 pt-2 border-t border-zinc-200">
            <div className="min-w-0">
              <label className="flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                <Bell className="w-3.5 h-3.5" strokeWidth={1.5} /> Daily briefing
              </label>
              <p className="text-xs text-zinc-500 mt-1">
                Get a browser notification with your top 3 tasks for the day.
              </p>
            </div>
            <Switch
              checked={form.notifications_enabled}
              onCheckedChange={(v) => setForm({ ...form, notifications_enabled: v })}
              data-testid="settings-notifications-toggle"
            />
          </div>

          {form.notifications_enabled && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
                Briefing time
              </label>
              <Select
                value={form.briefing_time}
                onValueChange={(v) => setForm({ ...form, briefing_time: v })}
              >
                <SelectTrigger className="rounded-none h-11" data-testid="settings-briefing-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-64">
                  {TIME_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="pt-3 border-t border-zinc-200">
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
              Default timer duration
            </label>
            <Select
              value={form.default_duration_minutes}
              onValueChange={(v) => setForm({ ...form, default_duration_minutes: v })}
            >
              <SelectTrigger className="rounded-none h-11" data-testid="settings-default-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="none">No default</SelectItem>
                <SelectItem value="15">15 min</SelectItem>
                <SelectItem value="25">25 min</SelectItem>
                <SelectItem value="45">45 min</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="pt-3 border-t border-zinc-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Tell a friend
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                Help keep BreakItDown free — share it with someone who needs it.
              </p>
            </div>
            <ShareAppLink
              medium="settings"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] font-medium px-3 py-2 border border-zinc-300 hover:border-zinc-900 text-zinc-900 whitespace-nowrap transition-colors"
              dataTestId="settings-share-btn"
            >
              Share
            </ShareAppLink>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-200">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] px-4 py-2.5 text-zinc-600 hover:bg-zinc-100"
            data-testid="settings-cancel"
          >
            <XIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] px-4 py-2.5 bg-zinc-900 hover:bg-[#002FA7] text-white disabled:opacity-40"
            data-testid="settings-save"
          >
            <Save className="w-3.5 h-3.5" strokeWidth={1.75} />
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
